import { useState, useEffect, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { emisionDteApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  FileText, Plus, Check, Edit2, Trash2, Loader2, Search,
  Send, CheckCircle, Clock, RefreshCw, AlertTriangle, Building,
  ClipboardList, Eye, X, Calendar, DollarSign, ArrowRight, Unlock,
  Mail, Printer, History, Zap, Link2, DownloadCloud,
} from 'lucide-react';
import { toast } from 'sonner';
import { SearchableCombobox } from '../components/ui/searchable-combobox';
import { fmtFecha } from '../lib/utils'; // Iter 292.g — formato DD-MM-YYYY canónico

// Iter 401 (Renzo) — OT centralizadora: SOLO por marca explícita `multicliente` (sin inferir por cliente, aunque sea CHS).
const esOtCentralizadora = (ot) => !!ot && !!ot.multicliente;

const fmt = (n) => Math.round(n || 0).toLocaleString('es-CL');

// Iter 291.k (Carlos) — días transcurridos desde que la proforma se facturó
// en Alegra y aún no llega el DTE del SII. Usado para alertar drafts atrasados.
const diasDesdeAlegra = (isoStr) => {
  if (!isoStr) return null;
  try {
    const created = new Date(isoStr);
    if (isNaN(created.getTime())) return null;
    const ms = Date.now() - created.getTime();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  } catch { return null; }
};

function DteOTCell({ dte, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const [saving, setSaving] = useState(false);

  const startEdit = () => { setVal(dte.ot_numero ? String(dte.ot_numero) : ''); setEditing(true); };

  const save = async () => {
    const n = val.trim() === '' ? null : parseInt(val, 10);
    if (val.trim() !== '' && (isNaN(n) || n <= 0)) { toast.error('Número de OT inválido'); return; }
    setSaving(true);
    try {
      await api.patch(`/facturacion/dtes-venta/${dte.folio}/ot`, { ot_numero: n, tipo_dte: dte.tipo_dte });
      toast.success(n ? `Factura ${dte.folio} → OT ${n}` : `OT quitada de la factura ${dte.folio}`);
      onSaved(dte, n);
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al asignar OT');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <input autoFocus value={val} onChange={(e) => setVal(e.target.value)} disabled={saving}
        className="w-16 border border-teal-400 rounded px-1 py-0.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-teal-500"
        placeholder="N° OT"
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        onBlur={() => { if (!saving) setEditing(false); }}
        data-testid={`ot-input-${dte.folio}`} />
    );
  }
  return dte.ot_numero ? (
    <button onClick={startEdit} className="font-mono font-semibold text-blue-700 hover:underline inline-flex items-center gap-1" title="Editar OT asignada" data-testid={`ot-cell-${dte.folio}`}>
      {dte.ot_numero} <Edit2 className="w-2.5 h-2.5 opacity-50" />
    </button>
  ) : (
    <button onClick={startEdit} className="text-gray-400 hover:text-teal-600 text-[10px] italic" title="Asignar OT" data-testid={`ot-cell-${dte.folio}`}>
      — asignar
    </button>
  );
}

export default function FacturaVenta() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [emitiendoDteId, setEmitiendoDteId] = useState(null);
  const rolNormalizado = (user?.rol || '').toLowerCase();
  const puedeAsociarManualmente = ['admin', 'administrador', 'gerencia'].includes(rolNormalizado);
  // Iter 400 (Renzo): misma regla que el backend (utils/permisos.puede_aprobar_proforma).
  const puedeAprobarProforma = ['admin', 'administrador', 'gerencia', 'contabilidad'].includes(rolNormalizado)
    || Boolean(user?.permisos?.contabilidad?.acciones?.proformas_aprobar);

  const initialTab = typeof window !== 'undefined' && window.location?.pathname?.includes('proforma') ? 'proformas' : 'dtes_pendientes';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);

  // Proformas
  const [proformas, setProformas] = useState([]);
  const [pendientesDTE, setPendientesDTE] = useState([]); // Iter 232.10 — alegra_invoice_id sin folio_dte
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');

  // DTEs Pendientes
  const [dtesPendientes, setDtesPendientes] = useState({ pendientes: [], ya_en_alegra: [], notas_credito: [], total_pendientes: 0, total_en_alegra: 0 });
  const [dtesLoading, setDtesLoading] = useState(false);
  const [selectedDtes, setSelectedDtes] = useState({});
  const [dteSearch, setDteSearch] = useState('');
  const [dteFiltroOT, setDteFiltroOT] = useState('todas');

  // Facturas Alegra
  const [facturasAlegra, setFacturasAlegra] = useState({ facturas: [], resumen: {}, meses_disponibles: [], mes_actual: '' });
  const [alegraLoading, setAlegraLoading] = useState(false);
  const [mesFilter, setMesFilter] = useState('');
  const [estadoAlegra, setEstadoAlegra] = useState('todos');
  const [clienteFilter, setClienteFilter] = useState('');
  const [expandedInv, setExpandedInv] = useState(null);

  // Create/Edit proforma modal
  const [formModal, setFormModal] = useState({ open: false, mode: 'create', proforma: null });
  const [formData, setFormData] = useState({
    ot_numero: '', cliente_rut: '', cliente_nombre: '', cliente_alegra_id: '',
    contacto: '',
    numero_oc_cliente: '', numero_hes: '', observaciones: '', condicion_pago: '',
    items: [{ descripcion: '', cantidad: 1, unidad: 'un', precio_unitario: 0, descuento: 0, alegra_item_id: '', ot_numero: '' }],
  });
  // Iter 232.12 — items de venta de Alegra (cuenta contable por línea).
  const [itemsAlegra, setItemsAlegra] = useState([]);
  const [saving, setSaving] = useState(false);

  // OT & Client dropdowns
  const [otList, setOtList] = useState([]);
  const [otFiltro, setOtFiltro] = useState('ejecucion');
  const [clienteList, setClienteList] = useState([]);
  // Iter 291.d (Carlos, feb-2026) — catálogo unificado `condiciones_pago`
  // (CP-*) desde `/condiciones-pago-crud?solo_nuevas=true`. Es el MISMO
  // que usan Proveedores y el maestro de Clientes: garantiza consistencia
  // entre las condiciones vistas en OC, Cliente y Proforma.
  const [condicionesPago, setCondicionesPago] = useState([]);

  // Detail modal
  const [detailModal, setDetailModal] = useState({ open: false, proforma: null });

  // Facturar modal
  const [facturarModal, setFacturarModal] = useState({ open: false, proforma: null });
  const [folioDTE, setFolioDTE] = useState('');
  const [facturando, setFacturando] = useState(false);

  // Iter 291.k (Carlos) — Asociar DTE manualmente cuando el XML no traía
  // `Contrato: OT-PRO<n>`. Solo Admin/Gerencia. Usa POST /facturacion/proformas/{id}/relinkear-dte.
  const [asociarDTEModal, setAsociarDTEModal] = useState({ open: false, proforma: null });
  const [asociarFolioInput, setAsociarFolioInput] = useState('');
  const [asociarMotivoInput, setAsociarMotivoInput] = useState('');
  const [asociando, setAsociando] = useState(false);

  // Iter 291.m (Carlos) — Sync folios desde Alegra (batch + por fila).
  const [syncingFolios, setSyncingFolios] = useState(false);
  const [syncingRowId, setSyncingRowId] = useState(null);

  // Contabilizar DTE modal
  const [contabModal, setContabModal] = useState({ open: false, dte: null });
  const [contabilizando, setContabilizando] = useState(false);

  // Desaprobar proforma modal
  const [desaprobarModal, setDesaprobarModal] = useState({ open: false, proforma: null });
  const [motivoDesaprobar, setMotivoDesaprobar] = useState('');
  const [desaprobando, setDesaprobando] = useState(false);

  // Email proforma state
  const [enviandoEmailId, setEnviandoEmailId] = useState(null);

  // ===== Data loaders =====
  const loadProformas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/facturacion/proformas');
      setProformas(res.data.proformas || []);
    } catch { toast.error('Error cargando proformas'); }
    finally { setLoading(false); }
  }, []);

  const loadDtesPendientes = useCallback(async () => {
    setDtesLoading(true);
    try {
      const res = await api.get('/facturacion/dtes-venta-pendientes');
      setDtesPendientes(res.data);
    } catch { toast.error('Error cargando DTEs de venta'); }
    finally { setDtesLoading(false); }
  }, []);

  const filtrarDtes = (list) => (list || []).filter(d => {
    if (dteFiltroOT === 'con_ot' && !d.ot_numero) return false;
    if (dteFiltroOT === 'sin_ot' && d.ot_numero) return false;
    const q = dteSearch.trim().toLowerCase();
    if (!q) return true;
    return String(d.folio).includes(q)
      || (d.receptor_nombre || '').toLowerCase().includes(q)
      || (d.receptor_rut || '').toLowerCase().includes(q)
      || String(d.ot_numero || '').includes(q);
  });

  const handleOTSaved = (dte, n) => {
    const upd = (x) => (x.folio === dte.folio && x.tipo_dte === dte.tipo_dte ? { ...x, ot_numero: n } : x);
    setDtesPendientes(prev => ({
      ...prev,
      pendientes: prev.pendientes.map(upd),
      ya_en_alegra: prev.ya_en_alegra.map(upd),
      notas_credito: (prev.notas_credito || []).map(upd),
    }));
  };

  const [autoAsignando, setAutoAsignando] = useState(false);
  const autoAsignarOT = async () => {
    setAutoAsignando(true);
    try {
      const res = await api.post('/facturacion/dtes-venta/auto-asignar-ot');
      const d = res.data;
      toast.success(`${d.asignadas.length} asignadas · ${d.ya_tenian_ot.length} ya tenían OT · ${d.sin_dte_en_sii.length} sin DTE en SII${d.ot_inexistente.length ? ` · ${d.ot_inexistente.length} con OT inexistente` : ''}`);
      loadDtesPendientes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en auto-asignación');
    } finally {
      setAutoAsignando(false);
    }
  };

  // Iter 232.10 — Proformas pendientes de DTE del SII (alegra_invoice_id sin folio_dte).
  const loadPendientesDTE = useCallback(async () => {
    try {
      const res = await api.get('/facturacion/proformas-pendientes-dte');
      setPendientesDTE(res.data?.items || []);
    } catch { /* silencioso, badge solo */ }
  }, []);

  const loadFacturasAlegra = useCallback(async (mes, estado, cliente) => {
    setAlegraLoading(true);
    try {
      const params = new URLSearchParams();
      if (mes) params.append('mes', mes);
      if (estado && estado !== 'todos') params.append('estado', estado);
      if (cliente) params.append('cliente', cliente);
      const res = await api.get(`/facturacion/facturas-alegra?${params.toString()}`);
      setFacturasAlegra(res.data);
      if (!mesFilter && res.data.mes_actual) setMesFilter(res.data.mes_actual);
    } catch { toast.error('Error cargando facturas de Alegra'); }
    finally { setAlegraLoading(false); }
  }, [mesFilter]);

  const loadOts = async (filtro) => {
    try {
      const res = await api.get(`/compras/ots-dropdown?filtro=${filtro}`);
      setOtList(res.data.ots || []);
    } catch { setOtList([]); }
  };

  const loadClientes = async () => {
    try {
      const res = await api.get('/facturacion/clientes-dropdown');
      setClienteList(res.data.clientes || []);
    } catch { setClienteList([]); }
  };

  useEffect(() => { loadDtesPendientes(); }, [loadDtesPendientes]);
  useEffect(() => { if (activeTab === 'proformas') loadProformas(); }, [activeTab, loadProformas]);
  useEffect(() => { if (activeTab === 'pendientes_dte') loadPendientesDTE(); }, [activeTab, loadPendientesDTE]);
  // Cargar count inicial para el badge
  useEffect(() => { loadPendientesDTE(); }, [loadPendientesDTE]);
  useEffect(() => {
    if (activeTab === 'emitidas') loadFacturasAlegra(mesFilter, estadoAlegra, clienteFilter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  // Iter 232 — formas de pago desde Alegra master list (master para proforma + factura).
  useEffect(() => {
    api.get('/condiciones-pago-crud', { params: { solo_nuevas: true } })
      .then((r) => setCondicionesPago(r.data || []))
      .catch(() => setCondicionesPago([]));
    // Iter 232.12 — items de venta Alegra (cuenta contable por línea).
    api.get('/facturacion/items-alegra').then((r) => setItemsAlegra(r.data?.items || [])).catch(() => setItemsAlegra([]));
  }, []);

  // ===== Proforma functions =====
  const openCreateModal = () => {
    setFormData({
      ot_numero: '', cliente_rut: '', cliente_nombre: '', contacto: '',
      numero_oc_cliente: '', numero_hes: '', observaciones: '', condicion_pago: '',
      items: [{ descripcion: '', cantidad: 1, unidad: 'un', precio_unitario: 0, descuento: 0, alegra_item_id: '', ot_numero: '' }],
    });
    setFormModal({ open: true, mode: 'create', proforma: null });
    setOtFiltro('ejecucion');
    loadOts('ejecucion');
    loadClientes();
  };

  const openEditModal = (p) => {
    setFormData({
      ot_numero: p.ot_numero || '', cliente_rut: p.cliente_rut || '', cliente_nombre: p.cliente_nombre || '',
      contacto: p.contacto || '',
      numero_oc_cliente: p.numero_oc_cliente || '', numero_hes: p.numero_hes || '',
      observaciones: p.observaciones || '', condicion_pago: p.condicion_pago || '',
      items: p.items?.length ? p.items : [{ descripcion: '', cantidad: 1, unidad: 'un', precio_unitario: 0, descuento: 0, alegra_item_id: '', ot_numero: '' }],
    });
    setFormModal({ open: true, mode: 'edit', proforma: p });
    setOtFiltro('ejecucion');
    loadOts('ejecucion');
    loadClientes();
  };

  const addItem = () => setFormData(d => ({
    ...d, items: [...d.items, { descripcion: '', cantidad: 1, unidad: 'un', precio_unitario: 0, descuento: 0, alegra_item_id: '', ot_numero: d.ot_numero || '' }],
  }));
  const removeItem = (idx) => setFormData(d => ({ ...d, items: d.items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, field, value) => {
    setFormData(d => {
      const items = [...d.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...d, items };
    });
  };

  const formNeto = formData.items.reduce((s, it) => s + (it.cantidad * it.precio_unitario) - (it.descuento || 0), 0);
  const formIVA = Math.round(formNeto * 0.19);
  const formTotal = Math.round(formNeto + formIVA);

  const handleClienteChange = (idOrRut) => {
    // Iter 291.f (Carlos) — el SearchableCombobox pasa el `id` del cliente
    // (único), evitando colisiones cuando hay RUTs duplicados (35 casos en
    // la base actual). Fallback a búsqueda por RUT para retrocompatibilidad.
    // Iter 292.d (Carlos, feb-2026) — Guarda contra match espurio: si el
    // usuario limpia el combobox (`idOrRut===''`), no matcheamos por RUT
    // vacío (colisiona con clientes sin RUT en la BD).
    const key = (idOrRut || '').trim();
    const cli = key
      ? (clienteList.find(c => c.id === key) || clienteList.find(c => c.rut === key))
      : null;
    setFormData(d => ({
      ...d,
      cliente_rut: cli?.rut || '',
      cliente_nombre: cli?.razon_social || '',
      contacto: d.contacto || cli?.contacto || '',
      condicion_pago: d.condicion_pago || cli?.condicion_pago || '',
    }));
  };

  // Iter 291.f — cliente actualmente elegido: se resuelve por RUT porque el
  // formData sólo persiste `cliente_rut` (no `cliente_id`).
  // Iter 292.d (Carlos, feb-2026) — Guarda contra match espurio cuando
  // `cliente_rut === ''`: si no hay RUT elegido, no buscamos un cliente por
  // RUT vacío (colisiona con clientes que tienen RUT en blanco en la BD).
  const clienteSeleccionado = (formData.cliente_rut || '').trim()
    ? (clienteList.find(c => c.rut === formData.cliente_rut) || null)
    : null;

  // Iter 291 — validación central del botón "Crear Proforma".
  // Devuelve string con la razón de bloqueo o null si todo está OK.
  const proformaValidationError = (() => {
    // Iter 402 (Renzo) — OT válida si hay OT general O todas las líneas con descripción traen su OT (multi-OT).
    const lineasConDesc = formData.items.filter((it) => (it.descripcion || '').toString().trim());
    const todasLineasConOt = lineasConDesc.length > 0 && lineasConDesc.every((it) => (it.ot_numero || '').toString().trim());
    if (!formData.ot_numero && !todasLineasConOt) return 'Falta OT (general, o una OT en cada línea)';
    if (!formData.cliente_rut) return 'Falta Cliente';
    if (!(formData.cliente_nombre || '').trim()) return 'Falta Nombre';
    if (!(formData.condicion_pago || '').trim()) return 'Falta Condición de Pago';
    if (clienteSeleccionado?.require_oc_cliente && !(formData.numero_oc_cliente || '').trim()) return 'Cliente exige N° OC';
    if (clienteSeleccionado?.require_hes && !(formData.numero_hes || '').trim()) return 'Cliente exige N° HES';
    if (!formData.items?.length) return 'Sin ítems';
    for (let i = 0; i < formData.items.length; i++) {
      const it = formData.items[i];
      if (!(it.alegra_item_id || '').toString().trim()) return `Ítem ${i + 1}: falta Cuenta Alegra`;
      if (!(it.descripcion || '').toString().trim()) return `Ítem ${i + 1}: falta Descripción`;
      if (!(Number(it.cantidad) > 0)) return `Ítem ${i + 1}: cantidad debe ser > 0`;
      if (!(Number(it.precio_unitario) > 0)) return `Ítem ${i + 1}: precio debe ser > 0`;
    }
    return null;
  })();

  const handleSave = async () => {
    if (!formData.cliente_rut || !formData.cliente_nombre) {
      toast.error('Falta el cliente — selecciona desde el dropdown (o elige una OT que tenga cliente Alegra asociado)', { duration: 6000 });
      console.warn('[Proforma Save] bloqueado: cliente_rut o cliente_nombre vacío', formData);
      return;
    }
    if (!formData.items.some(it => it.descripcion && it.precio_unitario > 0)) {
      toast.error('Agregue al menos un item con descripción y precio > 0', { duration: 6000 });
      console.warn('[Proforma Save] bloqueado: sin items válidos', formData.items);
      return;
    }

    // Iter 232.8 (Carlos) — warning soft si la proforma factura a un cliente distinto
    // al de la OT. Iter 401 (Renzo): en OT centralizadoras (multi-cliente o cliente CHS)
    // la diferencia es la regla, no un error → sin alerta.
    if (formData.ot_numero) {
      const ot = otList.find((o) => String(o.numero_ot) === String(formData.ot_numero));
      const otAlegraId = ot?.cliente_alegra_id;
      const otRut = ot?.cliente_rut;
      const proformaRut = formData.cliente_rut;
      const matchesOt = (otAlegraId && otAlegraId === formData.cliente_alegra_id) || (otRut && otRut === proformaRut);
      if (ot && !esOtCentralizadora(ot) && !matchesOt && (otRut || otAlegraId)) {
        const okConfirm = window.confirm(
          `⚠️ El cliente de esta proforma (${formData.cliente_nombre} / ${proformaRut}) NO coincide con el cliente de la OT ${formData.ot_numero} (${ot.cliente_nombre}).\n\n` +
          `Si esta OT centraliza servicios para varios clientes (transporte, vehículos, servicios recurrentes), márcala como "OT centralizadora" en su ficha y no volverás a ver este aviso.\n\n` +
          `¿Continuar guardando la proforma?`
        );
        if (!okConfirm) { toast.info('Guardado cancelado'); return; }
      }
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        ot_numero: formData.ot_numero ? parseInt(formData.ot_numero) : null,
        items: formData.items.filter(it => it.descripcion).map(it => ({
          ...it, cantidad: parseFloat(it.cantidad) || 1,
          precio_unitario: parseFloat(it.precio_unitario) || 0,
          descuento: parseFloat(it.descuento) || 0,
          ot_numero: it.ot_numero ? parseInt(it.ot_numero) : null,
        })),
      };
      if (formModal.mode === 'create') {
        const res = await api.post('/facturacion/proformas', payload);
        toast.success(res.data.message);
      } else {
        const res = await api.put(`/facturacion/proformas/${formModal.proforma.id}`, payload);
        toast.success(res.data.message);
      }
      setFormModal({ open: false, mode: 'create', proforma: null });
      loadProformas();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join('; ') : (err?.message || 'Error desconocido'));
      toast.error(`Error${status ? ' ' + status : ''}: ${msg}`, { duration: 8000 });
      console.error('[Proforma Save] error:', err?.response || err);
    }
    finally { setSaving(false); }
  };

  const handleAprobar = async (p) => {
    try {
      const res = await api.post(`/facturacion/proformas/${p.id}/aprobar`);
      // Iter 291 (Carlos, feb-2026) — copiar el número de proforma al portapapeles
      // al aprobar, para poder pegarlo en Alegra o donde el usuario necesite.
      const numeroAprobada = res.data?.proforma?.numero_proforma ?? p.numero_proforma;
      if (numeroAprobada) {
        try {
          await navigator.clipboard.writeText(String(numeroAprobada));
          toast.success(`${res.data.message} · N° ${numeroAprobada} copiado al portapapeles`, { duration: 6000 });
        } catch {
          toast.success(`${res.data.message} · N° ${numeroAprobada} (copia manual: no se pudo acceder al portapapeles)`, { duration: 6000 });
        }
      } else {
        toast.success(res.data.message);
      }
      loadProformas();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleEmitirDTE = async (p) => {
    setEmitiendoDteId(p.id);
    try {
      const res = await emisionDteApi.crear({ tipo_dte: '33', proforma_id: p.id, items: [], referencias: {} });
      toast.success(`Borrador DTE creado desde la proforma #${p.numero_proforma}`);
      navigate(`/emision-dte/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo crear el DTE desde la proforma');
    } finally {
      setEmitiendoDteId(null);
    }
  };


  const handleEliminar = async (p) => {
    if (!window.confirm(`Eliminar proforma #${p.numero_proforma}?`)) return;
    try {
      const res = await api.delete(`/facturacion/proformas/${p.id}`);
      toast.success(res.data.message); loadProformas();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleFacturar = async () => {
    if (!folioDTE.trim()) { toast.error('Ingrese el folio DTE'); return; }
    setFacturando(true);
    try {
      const res = await api.post(`/facturacion/proformas/${facturarModal.proforma.id}/facturar`, { folio_dte: folioDTE.trim() });
      toast.success(res.data.message);
      setFacturarModal({ open: false, proforma: null }); setFolioDTE('');
      loadProformas();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setFacturando(false); }
  };

  // Iter 291.k (Carlos) — Asociar manualmente el folio DTE a una proforma facturada en
  // Alegra cuya XML no traía referencia `OT-PRO<n>` (usa el endpoint relinkear-dte que
  // valida en dte_documentos + RUT del receptor).
  const handleAsociarDTEManual = async () => {
    const folio = asociarFolioInput.trim();
    const motivo = asociarMotivoInput.trim();
    if (!folio) { toast.error('Ingresa el folio DTE'); return; }
    if (folio === (asociarDTEModal.proforma?.folio_dte || '')) { toast.info('El folio ingresado es el mismo que ya tiene la proforma'); return; }
    if (motivo.length < 5) { toast.error('El motivo debe tener al menos 5 caracteres (auditoría)'); return; }
    setAsociando(true);
    try {
      const res = await api.post(
        `/facturacion/proformas/${asociarDTEModal.proforma.id}/relinkear-dte`,
        { folio_dte: folio, motivo },
      );
      toast.success(res.data.message);
      setAsociarDTEModal({ open: false, proforma: null });
      setAsociarFolioInput('');
      setAsociarMotivoInput('');
      loadProformas();
      loadPendientesDTE();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error asociando DTE');
    } finally {
      setAsociando(false);
    }
  };

  // Iter 232.8 (Carlos) — facturar directamente en Alegra, esperando DTE del SII.
  const handleFacturarEnAlegra = async (p) => {
    const ot = p.ot_numero || p.ot_info?.numero_ot || '';
    const contrato = ot ? `${ot}-PRO${p.numero_proforma}` : `PRO${p.numero_proforma}`;
    // Iter 232.11 — leer totales del objeto correcto (`totales.neto` / `totales.total`).
    const totales = p.totales || {};
    const neto = totales.neto || 0;
    const total = totales.total || 0;
    if (!window.confirm(
      `Crear factura en Alegra para proforma #${p.numero_proforma}?\n\n` +
      `Cliente: ${p.cliente_nombre}\n` +
      `Monto neto: $${neto.toLocaleString('es-CL')} (Total c/IVA: $${total.toLocaleString('es-CL')})\n\n` +
      `Al imprimir el DTE definitivo, escribe en "Contrato": ${contrato}\n` +
      `(El sistema asociará el DTE automáticamente cuando llegue del SII)`
    )) return;
    try {
      const res = await api.post(`/facturacion/proformas/${p.id}/facturar-en-alegra`);
      toast.success(res.data.message);
      window.prompt('Copia este texto para pegar en el campo Contrato del DTE:', res.data.contrato_ref_dte);
      loadProformas();
    } catch (err) {
      // Iter 232.11 — mostrar SIEMPRE algo visible, aunque el detail venga vacío.
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = detail || err?.message || 'Error desconocido al facturar en Alegra';
      toast.error(`Error${status ? ' ' + status : ''}: ${msg}`, { duration: 8000 });
      console.error('[FacturarEnAlegra] error:', err?.response || err);
    }
  };

  // Iter 291.m (Carlos) — Sincroniza folio desde Alegra para UNA proforma. Emite
  // la invoice si sigue en draft (pasa a "Por cobrar") y setea folio_dte + estado facturada.
  const handleSyncFolioAlegra = async (p) => {
    if (!window.confirm(
      `Sincronizar folio DTE desde Alegra para Proforma #${p.numero_proforma}?\n\n` +
      `Cliente: ${p.cliente_nombre}\nAlegra Invoice: ${p.alegra_invoice_id}\n\n` +
      `Si la factura sigue en Draft en Alegra se emitirá (pasará a "Por cobrar").`
    )) return;
    setSyncingRowId(p.id);
    try {
      const res = await api.post(`/facturacion/proformas/${p.id}/sync-folio-alegra`);
      toast.success(res.data.message || `Folio ${res.data.folio_dte} sincronizado`);
      loadProformas();
      loadPendientesDTE();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error sincronizando folio Alegra');
    } finally {
      setSyncingRowId(null);
    }
  };

  // Iter 291.m — Sincronizar folios en batch: recorre TODAS las proformas
  // con alegra_invoice_id + sin folio_dte.
  const handleSyncFoliosAlegraBatch = async () => {
    const pendientes = (proformas || []).filter(p => p.alegra_invoice_id && !p.folio_dte);
    if (pendientes.length === 0) {
      toast.info('No hay proformas pendientes de sincronizar folios con Alegra.');
      return;
    }
    if (!window.confirm(
      `Sincronizar folios Alegra para ${pendientes.length} proforma(s) pendiente(s)?\n\n` +
      `Las que sigan en Draft se emitirán en Alegra (Por cobrar).`
    )) return;
    setSyncingFolios(true);
    try {
      const res = await api.post('/facturacion/proformas/sync-folios-alegra');
      const d = res.data;
      const errores = d.con_error || 0;
      const esperando = d.esperando_dte || 0;
      const restantes = d.quedan_pendientes || 0;
      const sufijoEsperando = esperando > 0 ? ` · ${esperando} esperando su DTE (sin folio asignado)` : '';
      if (errores === 0) {
        const msg = `${d.sincronizadas || 0} proforma(s) sincronizada(s) con Alegra${sufijoEsperando}`
          + (restantes > esperando ? ` · Quedan ${restantes - esperando} pendientes (vuelve a hacer clic).` : '');
        toast.success(msg, { duration: restantes > 0 ? 8000 : 4000 });
      } else {
        toast.warning(`Sincronizadas: ${d.sincronizadas || 0} · Con error: ${errores}${sufijoEsperando}`, { duration: 8000 });
        // Log errores para diagnóstico
        (d.resultados || []).filter(r => !r.ok).forEach(r => {
          console.warn(`[sync_folios_alegra] PRO #${r.numero_proforma}: ${r.error}`);
        });
      }
      loadProformas();
      loadPendientesDTE();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error en sincronización batch');
    } finally {
      setSyncingFolios(false);
    }
  };

  const handleDesaprobar = async () => {
    const motivo = motivoDesaprobar.trim();
    if (motivo.length < 3) { toast.error('Ingrese un motivo (mínimo 3 caracteres)'); return; }
    setDesaprobando(true);
    try {
      const res = await api.post(
        `/facturacion/proformas/${desaprobarModal.proforma.id}/desaprobar`,
        { motivo }
      );
      toast.success(res.data.message);
      setDesaprobarModal({ open: false, proforma: null });
      setMotivoDesaprobar('');
      loadProformas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al desaprobar');
    } finally {
      setDesaprobando(false);
    }
  };

  const handleDescargarPDF = async (p) => {
    try {
      const res = await api.get(`/facturacion/proformas/${p.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const cliente = (p.cliente_nombre || '').replace(/\s+/g, '_').slice(0, 30);
      const filename = `Proforma_${p.numero_proforma}_${cliente}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Also open in new tab for instant view
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error generando PDF');
    }
  };

  const handleEnviarEmail = async (p) => {
    if (!window.confirm(`Enviar Proforma #${p.numero_proforma} (${p.cliente_nombre}) por correo a los destinatarios configurados?`)) return;
    setEnviandoEmailId(p.id);
    try {
      const res = await api.post(`/facturacion/proformas/${p.id}/enviar-email`, {});
      toast.success(res.data.message || 'Correo enviado');
      loadProformas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error enviando correo');
    } finally {
      setEnviandoEmailId(null);
    }
  };

  // ===== Contabilizar DTE =====
  const handleContabilizarDTE = async (dte) => {
    setContabilizando(true);
    try {
      const res = await api.post(`/facturacion/dtes-venta-pendientes/${dte.folio}/contabilizar`, {
        observaciones: '',
        item_id: contabModal.itemId || (dte.es_exenta ? '2' : '1'),
      });
      toast.success(res.data.message);
      setContabModal({ open: false, dte: null });
      loadDtesPendientes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error contabilizando'); }
    finally { setContabilizando(false); }
  };

  // ===== Estado badges =====
  const estadoBadge = (estado) => {
    const m = {
      borrador: { bg: 'bg-gray-100 text-gray-700', icon: <Edit2 className="w-3 h-3" />, label: 'Borrador' },
      aprobada: { bg: 'bg-green-100 text-green-700', icon: <CheckCircle className="w-3 h-3" />, label: 'Aprobada' },
      facturada: { bg: 'bg-blue-100 text-blue-700', icon: <FileText className="w-3 h-3" />, label: 'Facturada' },
      en_alegra: { bg: 'bg-purple-100 text-purple-700', icon: <Send className="w-3 h-3" />, label: 'En Alegra' },
      open: { bg: 'bg-amber-100 text-amber-700', icon: <Clock className="w-3 h-3" />, label: 'Abierta' },
      closed: { bg: 'bg-green-100 text-green-700', icon: <CheckCircle className="w-3 h-3" />, label: 'Pagada' },
      void: { bg: 'bg-red-100 text-red-700', icon: <X className="w-3 h-3" />, label: 'Anulada' },
    };
    const s = m[estado] || { bg: 'bg-gray-100 text-gray-600', icon: null, label: estado };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${s.bg}`}>
        {s.icon} {s.label}
      </span>
    );
  };

  // ===== Filtered proformas =====
  // Iter 291.e (Carlos, feb-2026) — el buscador ahora incluye también las
  // columnas N° OC del Cliente y N° HES para facilitar encontrar la Proforma
  // desde el número que trae la orden de compra o la hoja de entrada del cliente.
  const filtered = proformas.filter(p => {
    const q = (searchTerm || '').toLowerCase().trim();
    const matchSearch = !q ||
      p.cliente_nombre?.toLowerCase().includes(q) ||
      p.numero_proforma?.toString().includes(q) ||
      (p.numero_oc_cliente || '').toString().toLowerCase().includes(q) ||
      (p.numero_hes || '').toString().toLowerCase().includes(q);
    const matchEstado = filtroEstado === 'todos' || p.estado === filtroEstado;
    return matchSearch && matchEstado;
  });

  const counts = {
    total: proformas.length,
    borrador: proformas.filter(p => p.estado === 'borrador').length,
    aprobada: proformas.filter(p => p.estado === 'aprobada').length,
    facturada: proformas.filter(p => p.estado === 'facturada').length,
  };

  // Mes label
  const mesLabel = (mes) => {
    if (!mes) return '';
    const [y, m] = mes.split('-');
    const meses = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${meses[parseInt(m)] || m} ${y}`;
  };

  return (
    <div className="space-y-5" data-testid="factura-venta-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText className="w-6 h-6 text-teal-600" />
          Proforma de Factura
        </h1>
        <p className="text-sm text-muted-foreground">
          DTEs de venta pendientes, proformas y facturas emitidas en Alegra.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit" data-testid="venta-tab-switcher">
        <button onClick={() => setActiveTab('dtes_pendientes')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'dtes_pendientes' ? 'bg-white shadow-sm text-teal-700' : 'text-gray-600 hover:text-gray-900'}`}
          data-testid="tab-dtes-pendientes">
          <AlertTriangle className="w-4 h-4" /> DTEs Pendientes
          {dtesPendientes.total_pendientes > 0 && <span className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded-full">{dtesPendientes.total_pendientes}</span>}
        </button>
        <button onClick={() => setActiveTab('proformas')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'proformas' ? 'bg-white shadow-sm text-teal-700' : 'text-gray-600 hover:text-gray-900'}`}
          data-testid="tab-proformas">
          <ClipboardList className="w-4 h-4" /> Proformas
        </button>
        <button onClick={() => setActiveTab('pendientes_dte')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'pendientes_dte' ? 'bg-white shadow-sm text-teal-700' : 'text-gray-600 hover:text-gray-900'}`}
          data-testid="tab-pendientes-dte">
          <Clock className="w-4 h-4" /> Pendientes DTE
          {pendientesDTE.length > 0 && <span className="bg-emerald-100 text-emerald-700 text-xs px-1.5 py-0.5 rounded-full" data-testid="badge-pendientes-dte">{pendientesDTE.length}</span>}
        </button>
        <button onClick={() => setActiveTab('emitidas')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'emitidas' ? 'bg-white shadow-sm text-teal-700' : 'text-gray-600 hover:text-gray-900'}`}
          data-testid="tab-emitidas">
          <Send className="w-4 h-4" /> Facturas Emitidas
        </button>
      </div>

      {/* ======== TAB: DTEs PENDIENTES ======== */}
      {activeTab === 'dtes_pendientes' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="bg-white border rounded p-3 text-center">
              <p className="text-xs text-muted-foreground">Total DTEs Venta</p>
              <p className="text-xl font-bold">{dtesPendientes.total_dtes_venta || 0}</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-center">
              <p className="text-xs text-amber-700">Pendientes de Contabilizar</p>
              <p className="text-xl font-bold text-amber-700" data-testid="dtes-pendientes-count">{dtesPendientes.total_pendientes}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded p-3 text-center">
              <p className="text-xs text-green-700">Ya en Alegra</p>
              <p className="text-xl font-bold text-green-700">{dtesPendientes.total_en_alegra}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Button variant="outline" onClick={loadDtesPendientes} disabled={dtesLoading} data-testid="refresh-dtes-pendientes">
              <RefreshCw className={`w-4 h-4 mr-2 ${dtesLoading ? 'animate-spin' : ''}`} /> Actualizar
            </Button>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={dteSearch} onChange={(e) => setDteSearch(e.target.value)}
                placeholder="Buscar folio, cliente, RUT u OT..." className="h-9 pl-7 w-64 text-xs" data-testid="dte-search-input" />
            </div>
            <select value={dteFiltroOT} onChange={(e) => setDteFiltroOT(e.target.value)}
              className="h-9 border border-input rounded-md px-2 text-xs bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="dte-filtro-ot">
              <option value="todas">OT: todas</option>
              <option value="con_ot">Con OT asignada</option>
              <option value="sin_ot">Sin OT asignada</option>
            </select>
            <Button variant="outline" onClick={autoAsignarOT} disabled={autoAsignando}
              className="text-teal-700 border-teal-300 hover:bg-teal-50" data-testid="btn-auto-asignar-ot">
              {autoAsignando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Auto-asignar desde proformas
            </Button>
            <p className="text-xs text-gray-500">
              La OT asignada define dónde suma el Facturado en Activo Circulante. Clic en la columna OT para asignar/editar.
            </p>
          </div>

          {dtesLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          )}

          {!dtesLoading && dtesPendientes.pendientes.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs" data-testid="dtes-pendientes-table">
                <thead>
                  <tr className="bg-amber-600 text-white text-left">
                    <th className="px-3 py-2">Folio</th>
                    <th className="px-3 py-2">Fecha</th>
                    <th className="px-3 py-2">Cliente (Receptor)</th>
                    <th className="px-3 py-2">RUT</th>
                    <th className="px-3 py-2 text-right">Neto</th>
                    <th className="px-3 py-2 text-right">IVA</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2">OC Ref.</th>
                    <th className="px-3 py-2">OT</th>
                    <th className="px-3 py-2 text-center">Accion</th>
                  </tr>
                </thead>
                <tbody>
                  {filtrarDtes(dtesPendientes.pendientes).map((d, i) => (
                    <tr key={d.folio} className="border-t hover:bg-amber-50/30" data-testid={`dte-pend-${d.folio}`}>
                      <td className="px-3 py-2 font-mono font-bold text-amber-700">
                        {d.folio}
                        {d.es_exenta && (
                          <span className="ml-1 text-[8px] font-semibold bg-purple-100 text-purple-700 border border-purple-300 rounded px-1 align-middle" data-testid={`pend-exenta-${d.folio}`}>EX</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{fmtFecha(d.fecha_emision)}</td>
                      <td className="px-3 py-2 truncate max-w-[200px]">{d.receptor_nombre}</td>
                      <td className="px-3 py-2 font-mono text-[10px] text-gray-500">{d.receptor_rut}</td>
                      <td className="px-3 py-2 text-right font-mono">${fmt(d.neto)}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-500">${fmt(d.iva)}</td>
                      <td className="px-3 py-2 text-right font-mono font-medium">${fmt(d.total)}</td>
                      <td className="px-3 py-2 text-[10px]">{d.orden_compra || '-'}</td>
                      <td className="px-3 py-2"><DteOTCell dte={d} onSaved={handleOTSaved} /></td>
                      <td className="px-3 py-2 text-center">
                        <Button size="sm" className="h-6 text-[10px] bg-teal-600 hover:bg-teal-700"
                          onClick={() => setContabModal({ open: true, dte: d, itemId: d.es_exenta ? '2' : '1' })}
                          data-testid={`contabilizar-${d.folio}`}>
                          <ArrowRight className="w-3 h-3 mr-1" /> Contabilizar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!dtesLoading && dtesPendientes.pendientes.length === 0 && (
            <div className="text-center py-12 bg-green-50 rounded-lg border border-green-200">
              <CheckCircle className="w-12 h-12 mx-auto text-green-400 mb-3" />
              <p className="text-green-700 font-medium">Todos los DTEs de venta estan contabilizados</p>
              <p className="text-xs text-green-600 mt-1">No hay facturas pendientes de ingresar a Alegra</p>
            </div>
          )}

          {/* Ya en Alegra section */}
          {dtesPendientes.ya_en_alegra?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-medium text-gray-600 mb-2">DTEs ya registrados en Alegra ({dtesPendientes.total_en_alegra})</p>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-green-50 text-left">
                      <th className="px-3 py-2">Folio</th>
                      <th className="px-3 py-2">Fecha</th>
                      <th className="px-3 py-2">Cliente</th>
                      <th className="px-3 py-2 text-right">Neto</th>
                      <th className="px-3 py-2 text-right">Total</th>
                      <th className="px-3 py-2">OT</th>
                      <th className="px-3 py-2 text-center">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtrarDtes(dtesPendientes.ya_en_alegra).map((d) => (
                      <tr key={`${d.folio}-${d.tipo_dte}`} className="border-t hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono text-green-700">{d.folio}</td>
                        <td className="px-3 py-2 text-gray-600">{fmtFecha(d.fecha_emision)}</td>
                        <td className="px-3 py-2 truncate max-w-[200px]">{d.receptor_nombre}</td>
                        <td className="px-3 py-2 text-right font-mono text-blue-700" title="Valor neto (sin IVA) — es el que suma al Facturado del Activo Circulante">${fmt(d.neto)}</td>
                        <td className="px-3 py-2 text-right font-mono">${fmt(d.total)}</td>
                        <td className="px-3 py-2"><DteOTCell dte={d} onSaved={handleOTSaved} /></td>
                        <td className="px-3 py-2 text-center">
                          <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-[10px]">
                            <CheckCircle className="w-3 h-3 inline mr-1" />En Alegra
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(dtesPendientes.notas_credito || []).length > 0 && (
            <div className="bg-white border rounded-lg overflow-hidden" data-testid="tabla-ncs-venta">
              <div className="px-4 py-2 bg-red-50 border-b flex items-center justify-between">
                <h3 className="text-sm font-semibold text-red-800">Notas de Crédito emitidas — restan del Facturado en el AC</h3>
                <span className="text-xs text-red-600 font-semibold">{filtrarDtes(dtesPendientes.notas_credito).length} NC</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-red-50/60 text-left">
                      <th className="px-3 py-2">Folio NC</th>
                      <th className="px-3 py-2">Fecha</th>
                      <th className="px-3 py-2">Cliente</th>
                      <th className="px-3 py-2">Ref. Factura</th>
                      <th className="px-3 py-2 text-right">Neto</th>
                      <th className="px-3 py-2 text-right">Total</th>
                      <th className="px-3 py-2">OT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtrarDtes(dtesPendientes.notas_credito).map((d) => (
                      <tr key={`nc-${d.folio}`} className="border-t hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono text-red-700">{d.folio}</td>
                        <td className="px-3 py-2 text-gray-600">{fmtFecha(d.fecha_emision)}</td>
                        <td className="px-3 py-2 truncate max-w-[200px]">{d.receptor_nombre}</td>
                        <td className="px-3 py-2 text-[10px] text-gray-600">{d.referencia || '-'}</td>
                        <td className="px-3 py-2 text-right font-mono text-red-600">-${fmt(Math.abs(d.neto))}</td>
                        <td className="px-3 py-2 text-right font-mono text-red-600">-${fmt(Math.abs(d.total))}</td>
                        <td className="px-3 py-2"><DteOTCell dte={d} onSaved={handleOTSaved} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ======== TAB: PROFORMAS ======== */}
      {activeTab === 'proformas' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border rounded p-3 text-center">
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-xl font-bold" data-testid="total-proformas">{counts.total}</p>
            </div>
            <div className="bg-gray-50 border rounded p-3 text-center">
              <p className="text-xs text-gray-600">Borradores</p>
              <p className="text-lg font-bold text-gray-700">{counts.borrador}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded p-3 text-center">
              <p className="text-xs text-green-700">Aprobadas</p>
              <p className="text-lg font-bold text-green-700">{counts.aprobada}</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-center">
              <p className="text-xs text-blue-700">Facturadas</p>
              <p className="text-lg font-bold text-blue-700">{counts.facturada}</p>
            </div>
          </div>

          <div className="bg-white border rounded-lg p-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[200px] max-w-sm">
                <label className="block text-xs font-medium mb-1 text-gray-600">Buscar</label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input placeholder="Cliente, N proforma, OC o HES..." value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" data-testid="search-proforma" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600">Estado</label>
                <div className="flex gap-1">
                  {['todos', 'borrador', 'aprobada', 'facturada'].map(val => (
                    <button key={val} onClick={() => setFiltroEstado(val)}
                      className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${filtroEstado === val ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                      data-testid={`filter-${val}`}>{val === 'todos' ? 'Todos' : val.charAt(0).toUpperCase() + val.slice(1)}</button>
                  ))}
                </div>
              </div>
              <Button variant="outline" onClick={loadProformas} disabled={loading} data-testid="refresh-proformas">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              {/* Iter 291.m (Carlos) — Sync folios Alegra en batch. Solo visible cuando
                  hay al menos una proforma con alegra_invoice_id sin folio_dte. */}
              {puedeAsociarManualmente && proformas.some(p => p.alegra_invoice_id && !p.folio_dte) && (
                <Button
                  variant="outline"
                  onClick={handleSyncFoliosAlegraBatch}
                  disabled={syncingFolios}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="sync-folios-alegra-btn"
                  title="Traer folios desde Alegra para todas las proformas facturadas sin folio_dte, emitiendo las que sigan en draft.">
                  {syncingFolios
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <DownloadCloud className="w-4 h-4 mr-2" />}
                  Sincronizar folios Alegra
                  <span className="ml-2 bg-emerald-100 text-emerald-800 text-[10px] px-1.5 py-0.5 rounded-full">
                    {proformas.filter(p => p.alegra_invoice_id && !p.folio_dte).length}
                  </span>
                </Button>
              )}
              <Button className="bg-teal-600 hover:bg-teal-700" onClick={openCreateModal} data-testid="nueva-proforma-btn">
                <Plus className="w-4 h-4 mr-2" /> Nueva Proforma
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
          ) : filtered.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs" data-testid="proformas-table">
                <thead>
                  <tr className="bg-gray-800 text-white text-left">
                    <th className="px-3 py-2">N</th>
                    <th className="px-3 py-2">Fecha</th>
                    <th className="px-3 py-2">Cliente</th>
                    <th className="px-3 py-2 text-center">OT</th>
                    <th className="px-3 py-2">OC/OS</th>
                    <th className="px-3 py-2">HES</th>
                    <th className="px-3 py-2 text-right">Neto</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-center">Estado</th>
                    <th className="px-3 py-2">DTE</th>
                    <th className="px-3 py-2 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p.id} className="border-t hover:bg-gray-50" data-testid={`proforma-row-${p.numero_proforma}`}>
                      <td className="px-3 py-2 font-mono font-bold text-teal-700">#{p.numero_proforma}</td>
                      <td className="px-3 py-2 text-gray-600">{p.created_at?.split('T')[0]}</td>
                      <td className="px-3 py-2 truncate max-w-[160px]" title={p.cliente_nombre}>{p.cliente_nombre}</td>
                      <td className="px-3 py-2 text-center">{p.ot_numero ? <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-[10px]">OT-{p.ot_numero}</span> : '-'}</td>
                      <td className="px-3 py-2 text-[10px]">{p.numero_oc_cliente || '-'}</td>
                      <td className="px-3 py-2 text-[10px]">{p.numero_hes || '-'}</td>
                      <td className="px-3 py-2 text-right font-mono">${fmt(p.totales?.neto)}</td>
                      <td className="px-3 py-2 text-right font-mono font-medium">${fmt(p.totales?.total)}</td>
                      <td className="px-3 py-2 text-center">{estadoBadge(p.estado)}</td>
                      <td className="px-3 py-2 font-mono text-teal-700 font-bold">
                        {puedeAsociarManualmente ? (
                          <button
                            onClick={() => {
                              setAsociarDTEModal({ open: true, proforma: p });
                              setAsociarFolioInput(p.folio_dte || '');
                              setAsociarMotivoInput('');
                            }}
                            className="inline-flex items-center gap-1 hover:underline hover:text-teal-900"
                            title={p.folio_dte ? 'Modificar folio DTE' : 'Asignar folio DTE'}
                            data-testid={`edit-dte-proforma-${p.numero_proforma}`}>
                            {p.folio_dte || <span className="text-gray-400 italic font-sans font-normal text-[10px]">asignar</span>}
                            <Edit2 className="w-2.5 h-2.5 opacity-40" />
                          </button>
                        ) : (p.folio_dte || '-')}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => setDetailModal({ open: true, proforma: p })}
                            className="p-1 rounded hover:bg-blue-50 text-blue-600" title="Ver" data-testid={`view-proforma-${p.numero_proforma}`}>
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          {/* Iter 291.k (Carlos) — Pill "Alegra pendiente" + botón asociar folio manualmente.
                              Visible en cualquier estado mientras exista alegra_invoice_id sin folio_dte
                              (típicamente `facturada_en_alegra`, pero se muestra también si el flujo antiguo
                              lo dejó en `aprobada` con invoice creada). */}
                          {p.alegra_invoice_id && !p.folio_dte && (() => {
                            const dias = diasDesdeAlegra(p.alegra_invoice_creado_at);
                            const atrasada = dias !== null && dias > 3;
                            const contratoRef = p.contrato_ref_dte || `${p.ot_numero || ''}-PRO${p.numero_proforma}`;
                            const tooltipBase = `Pendiente DTE del SII. Contrato a copiar en factura: ${contratoRef}`;
                            const tooltip = dias !== null
                              ? `${tooltipBase}\nFacturada en Alegra hace ${dias} día${dias === 1 ? '' : 's'}.`
                              : tooltipBase;
                            return (
                              <span title={tooltip}
                                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium tabular-nums ${
                                  atrasada
                                    ? 'bg-red-100 text-red-700 border border-red-300'
                                    : 'bg-emerald-50 text-emerald-700'
                                }`}
                                data-testid={`alegra-pendiente-${p.numero_proforma}`}>
                                {atrasada && <AlertTriangle className="w-3 h-3" data-testid={`alegra-draft-alerta-${p.numero_proforma}`} />}
                                Alegra {p.alegra_invoice_id}
                                {dias !== null && (
                                  <span className={atrasada ? 'font-bold' : 'opacity-70'} data-testid={`alegra-dias-${p.numero_proforma}`}>
                                    · {dias}d
                                  </span>
                                )}
                              </span>
                            );
                          })()}
                          {p.alegra_invoice_id && !p.folio_dte && puedeAsociarManualmente && (
                            <button onClick={() => handleSyncFolioAlegra(p)}
                              disabled={syncingRowId === p.id || syncingFolios}
                              className="p-1 rounded hover:bg-emerald-50 text-emerald-600 disabled:opacity-50"
                              title="Sincronizar folio desde Alegra (emite la factura si sigue en draft)"
                              data-testid={`sync-folio-alegra-${p.numero_proforma}`}>
                              {syncingRowId === p.id
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <DownloadCloud className="w-3.5 h-3.5" />}
                            </button>
                          )}
                          {p.alegra_invoice_id && !p.folio_dte && puedeAsociarManualmente && (
                            <button onClick={() => {
                              setAsociarDTEModal({ open: true, proforma: p });
                              setAsociarFolioInput('');
                              setAsociarMotivoInput('');
                            }}
                              className="p-1 rounded hover:bg-indigo-50 text-indigo-600"
                              title="Asociar folio DTE manualmente (cuando el XML no traía Contrato: OT-PRO)"
                              data-testid={`asociar-dte-manual-${p.numero_proforma}`}>
                              <Link2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {p.estado === 'borrador' && (
                            <>
                              <button onClick={() => openEditModal(p)} className="p-1 rounded hover:bg-amber-50 text-amber-600" title="Editar"><Edit2 className="w-3.5 h-3.5" /></button>
                              {puedeAprobarProforma && (
                                <button onClick={() => handleAprobar(p)} className="p-1 rounded hover:bg-green-50 text-green-600" title="Aprobar" data-testid={`aprobar-proforma-${p.numero_proforma}`}><Check className="w-3.5 h-3.5" /></button>
                              )}
                              <button onClick={() => handleEliminar(p)} className="p-1 rounded hover:bg-red-50 text-red-600" title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></button>
                            </>
                          )}
                          {p.estado === 'aprobada' && (
                            <>
                              <button onClick={() => handleEmitirDTE(p)}
                                disabled={emitiendoDteId === p.id}
                                className="p-1 rounded hover:bg-indigo-50 text-indigo-600 disabled:opacity-50" title="Emitir DTE (factura/guía) desde esta proforma" data-testid={`emitir-dte-proforma-${p.numero_proforma}`}>
                                {emitiendoDteId === p.id
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  : <Send className="w-3.5 h-3.5" />}
                              </button>
                              <button onClick={() => handleDescargarPDF(p)}
                                className="p-1 rounded hover:bg-blue-50 text-blue-600" title="Descargar/Imprimir PDF" data-testid={`pdf-proforma-${p.numero_proforma}`}>
                                <Printer className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => handleEnviarEmail(p)}
                                disabled={enviandoEmailId === p.id}
                                className="p-1 rounded hover:bg-purple-50 text-purple-600 disabled:opacity-50" title="Enviar por correo" data-testid={`email-proforma-${p.numero_proforma}`}>
                                {enviandoEmailId === p.id
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  : <Mail className="w-3.5 h-3.5" />}
                              </button>
                              {Array.isArray(p.envios_email) && p.envios_email.some(e => e.estado === 'enviado') && (
                                <span title={`Enviado ${p.envios_email.filter(e => e.estado === 'enviado').length} vez(ces). Último: ${(p.envios_email.filter(e => e.estado === 'enviado').slice(-1)[0]?.fecha || '').split('T')[0]}`}
                                  data-testid={`email-sent-badge-${p.numero_proforma}`}>
                                  <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                                </span>
                              )}
                              {/* Iter 232.9 (Carlos) — botón manual solo si la proforma NO ha sido facturada en Alegra (regularización de casos viejos). */}
                              {!p.alegra_invoice_id && (
                                <button onClick={() => { setFacturarModal({ open: true, proforma: p }); setFolioDTE(''); }}
                                  className="p-1 rounded hover:bg-amber-50 text-amber-600" title="Registrar folio DTE (regularizar — solo si el DTE ya fue emitido por fuera de CHIRINO)" data-testid={`facturar-proforma-${p.numero_proforma}`}>
                                  <FileText className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {!p.alegra_invoice_id && (
                                <button onClick={() => handleFacturarEnAlegra(p)}
                                  className="p-1 rounded hover:bg-emerald-50 text-emerald-600" title="Facturar en Alegra (sin folio DTE — se asociará automáticamente cuando llegue del SII)" data-testid={`facturar-alegra-proforma-${p.numero_proforma}`}>
                                  <Zap className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {!p.folio_dte && !p.alegra_invoice_id && (
                                <button
                                  onClick={() => { setDesaprobarModal({ open: true, proforma: p }); setMotivoDesaprobar(''); }}
                                  className="p-1 rounded hover:bg-orange-50 text-orange-600"
                                  title="Desaprobar (volver a borrador)"
                                  data-testid={`desaprobar-proforma-${p.numero_proforma}`}>
                                  <Unlock className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </>
                          )}
                          {p.estado === 'facturada' && (
                            <button onClick={() => handleDescargarPDF(p)}
                              className="p-1 rounded hover:bg-blue-50 text-blue-600" title="Descargar PDF" data-testid={`pdf-proforma-${p.numero_proforma}`}>
                              <Printer className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 border-t font-medium text-xs">
                    <td colSpan={6} className="px-3 py-2 text-right text-gray-600">Total ({filtered.length}):</td>
                    <td className="px-3 py-2 text-right font-mono">${fmt(filtered.reduce((s, p) => s + (p.totales?.neto || 0), 0))}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">${fmt(filtered.reduce((s, p) => s + (p.totales?.total || 0), 0))}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-600">No hay proformas</p>
              <Button className="mt-3 bg-teal-600 hover:bg-teal-700" onClick={openCreateModal}>
                <Plus className="w-4 h-4 mr-2" /> Crear Proforma
              </Button>
            </div>
          )}
        </>
      )}

      {/* ======== TAB: PENDIENTES DTE (Iter 232.10) ======== */}
      {activeTab === 'pendientes_dte' && (
        <div className="space-y-3" data-testid="tab-content-pendientes-dte">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
            Proformas facturadas en Alegra que aún <b>esperan el folio DTE del SII</b>.
            Cuando llegue el DTE por correo y traiga el campo <code className="bg-white px-1 rounded">Contrato: OT-PRO&lt;n&gt;</code>, se asociará automáticamente.
          </div>
          {pendientesDTE.length === 0 ? (
            <div className="bg-white border rounded-lg p-8 text-center text-sm text-muted-foreground">
              No hay proformas esperando DTE. 🎉
            </div>
          ) : (
            <div className="bg-white border rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-left text-gray-600">
                  <tr>
                    <th className="px-2 py-2">Proforma</th>
                    <th className="px-2 py-2">OT</th>
                    <th className="px-2 py-2">Cliente</th>
                    <th className="px-2 py-2 text-right">Total c/IVA</th>
                    <th className="px-2 py-2">Alegra ID</th>
                    <th className="px-2 py-2">Contrato a copiar</th>
                    <th className="px-2 py-2 text-right">Días espera</th>
                  </tr>
                </thead>
                <tbody>
                  {pendientesDTE.map((p) => (
                    <tr key={p.id} className="border-t hover:bg-gray-50" data-testid={`pendiente-dte-${p.numero_proforma}`}>
                      <td className="px-2 py-2 font-medium tabular-nums">#{p.numero_proforma}</td>
                      <td className="px-2 py-2 tabular-nums">{p.ot_numero || '-'}</td>
                      <td className="px-2 py-2">{p.cliente_nombre}<div className="text-[10px] text-muted-foreground">{p.cliente_rut}</div></td>
                      <td className="px-2 py-2 text-right tabular-nums">${(p.total_con_iva || 0).toLocaleString('es-CL')}</td>
                      <td className="px-2 py-2 tabular-nums">{p.alegra_invoice_id}</td>
                      <td className="px-2 py-2">
                        <code className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded text-[11px] font-mono" title="Click para copiar"
                          onClick={() => { navigator.clipboard?.writeText(p.contrato_ref_dte); toast.success('Copiado al portapapeles'); }}
                          style={{ cursor: 'pointer' }}>
                          {p.contrato_ref_dte}
                        </code>
                      </td>
                      <td className={`px-2 py-2 text-right tabular-nums font-medium ${p.dias_espera >= 7 ? 'text-amber-700' : p.dias_espera >= 15 ? 'text-red-600' : 'text-gray-600'}`}>
                        {p.dias_espera}d
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ======== TAB: FACTURAS EMITIDAS (ALEGRA) ======== */}
      {activeTab === 'emitidas' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white border rounded-lg p-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600">Mes</label>
                <select value={mesFilter} onChange={(e) => setMesFilter(e.target.value)}
                  className="border rounded px-2 py-1.5 text-xs min-w-[140px]" data-testid="mes-filter-alegra">
                  {(facturasAlegra.meses_disponibles || []).map(m => (
                    <option key={m} value={m}>{mesLabel(m)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600">Estado</label>
                <div className="flex gap-1">
                  {[
                    { val: 'todos', label: 'Todos' },
                    { val: 'open', label: 'Abiertas' },
                    { val: 'closed', label: 'Pagadas' },
                    { val: 'void', label: 'Anuladas' },
                  ].map(opt => (
                    <button key={opt.val} onClick={() => setEstadoAlegra(opt.val)}
                      className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${estadoAlegra === opt.val ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                      data-testid={`filter-alegra-${opt.val}`}>{opt.label}</button>
                  ))}
                </div>
              </div>
              <div className="flex-1 min-w-[160px] max-w-xs">
                <label className="block text-xs font-medium mb-1 text-gray-600">Cliente</label>
                <Input placeholder="Filtrar por cliente..." value={clienteFilter}
                  onChange={(e) => setClienteFilter(e.target.value)} className="h-8 text-xs" data-testid="cliente-filter-alegra" />
              </div>
              <Button className="bg-teal-600 hover:bg-teal-700 h-8"
                onClick={() => loadFacturasAlegra(mesFilter, estadoAlegra, clienteFilter)}
                disabled={alegraLoading} data-testid="buscar-alegra-btn">
                {alegraLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                Buscar
              </Button>
            </div>
          </div>

          {/* Summary (excluding anuladas) */}
          {facturasAlegra.resumen && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-white border rounded p-3 text-center">
                <p className="text-xs text-muted-foreground">Facturas ({mesLabel(facturasAlegra.mes_actual)})</p>
                <p className="text-xl font-bold" data-testid="total-alegra">{facturasAlegra.resumen.total_facturas || 0}</p>
                <p className="text-[9px] text-gray-400">{facturasAlegra.resumen.facturas_activas || 0} activas</p>
              </div>
              <div className="bg-teal-50 border border-teal-200 rounded p-3 text-center">
                <p className="text-xs text-teal-700">Monto Total</p>
                <p className="text-lg font-bold text-teal-700">${fmt(facturasAlegra.resumen.total_monto)}</p>
                <p className="text-[9px] text-gray-400">Neto: ${fmt(facturasAlegra.resumen.total_neto)}</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded p-3 text-center">
                <p className="text-xs text-green-700">Pagado</p>
                <p className="text-lg font-bold text-green-700">${fmt(facturasAlegra.resumen.total_pagado)}</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-center">
                <p className="text-xs text-amber-700">Saldo Pendiente</p>
                <p className="text-lg font-bold text-amber-700">${fmt(facturasAlegra.resumen.total_saldo)}</p>
              </div>
              <div className="bg-gray-50 border rounded p-3 text-center">
                <p className="text-xs text-gray-600">Abiertas / Pagadas / Anuladas</p>
                <p className="text-lg font-bold">
                  <span className="text-amber-600">{facturasAlegra.resumen.estados?.open || 0}</span>
                  {' / '}
                  <span className="text-green-600">{facturasAlegra.resumen.estados?.closed || 0}</span>
                  {' / '}
                  <span className="text-red-400">{facturasAlegra.resumen.estados?.void || 0}</span>
                </p>
              </div>
            </div>
          )}

          {/* Table */}
          {alegraLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
          ) : (facturasAlegra.facturas || []).length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs" data-testid="facturas-alegra-table">
                <thead>
                  <tr className="bg-gray-800 text-white text-left">
                    <th className="px-1 py-2 w-8"></th>
                    <th className="px-3 py-2">N Factura</th>
                    <th className="px-3 py-2">Fecha</th>
                    <th className="px-3 py-2">Vencimiento</th>
                    <th className="px-3 py-2">Cliente</th>
                    <th className="px-3 py-2">Plazo</th>
                    <th className="px-3 py-2 text-right">Neto</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-right">Pagado</th>
                    <th className="px-3 py-2 text-right">Saldo</th>
                    <th className="px-3 py-2 text-center">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {(facturasAlegra.facturas || []).map((f) => {
                    const isExpanded = expandedInv === f.alegra_id;
                    const isVoid = f.estado === 'void';
                    return (
                      <Fragment key={f.alegra_id}>
                        <tr
                          className={`border-t hover:bg-gray-50 cursor-pointer ${isVoid ? 'opacity-40 line-through' : ''} ${isExpanded ? 'bg-teal-50/50' : ''}`}
                          onClick={() => setExpandedInv(isExpanded ? null : f.alegra_id)}
                          data-testid={`alegra-inv-${f.numero}`}>
                          <td className="px-1 py-2 text-center">
                            <span className={`text-gray-400 text-[10px] transition-transform inline-block ${isExpanded ? 'rotate-90' : ''}`}>&#9654;</span>
                          </td>
                          <td className="px-3 py-2 font-mono font-bold text-teal-700">{f.numero}</td>
                          <td className="px-3 py-2 text-gray-600">{f.fecha}</td>
                          <td className="px-3 py-2 text-gray-500">{f.fecha_vencimiento || '-'}</td>
                          <td className="px-3 py-2 truncate max-w-[180px]" title={f.cliente_nombre}>{f.cliente_nombre}</td>
                          <td className="px-3 py-2 text-[10px] text-gray-500">{f.plazo_pago || '-'}</td>
                          <td className="px-3 py-2 text-right font-mono">${fmt(f.subtotal)}</td>
                          <td className="px-3 py-2 text-right font-mono font-medium">${fmt(f.total)}</td>
                          <td className="px-3 py-2 text-right font-mono text-green-600">{f.monto_pagado > 0 ? `$${fmt(f.monto_pagado)}` : '-'}</td>
                          <td className="px-3 py-2 text-right font-mono">
                            {f.saldo > 0 ? <span className="text-amber-600 font-medium">${fmt(f.saldo)}</span> : <span className="text-green-600">$0</span>}
                          </td>
                          <td className="px-3 py-2 text-center">{estadoBadge(f.estado)}</td>
                        </tr>
                        {isExpanded && (
                          <tr className="bg-gray-50/80">
                            <td colSpan={11} className="px-4 py-3">
                              <div className="grid grid-cols-3 gap-4 mb-3">
                                <div className="text-[10px] space-y-1">
                                  <p><strong>Forma de Pago:</strong> {f.forma_pago || '-'}</p>
                                  <p><strong>Centro Costo:</strong> {f.centro_costo || '-'}</p>
                                  <p><strong>Tipo:</strong> {f.tipo || '-'}</p>
                                </div>
                                <div className="text-[10px] space-y-1">
                                  <p><strong>Vendedor:</strong> {f.vendedor || '-'}</p>
                                  <p><strong>ID Alegra:</strong> {f.alegra_id}</p>
                                  <p><strong>ID Cliente:</strong> {f.cliente_id}</p>
                                </div>
                                <div className="text-[10px] space-y-1">
                                  {f.observaciones_alegra && <p><strong>Obs:</strong> {f.observaciones_alegra}</p>}
                                  <p><strong>Impuestos:</strong> ${fmt(f.impuestos)}</p>
                                </div>
                              </div>
                              {f.items_detalle?.length > 0 && (
                                <div>
                                  <p className="text-[10px] font-bold text-gray-600 mb-1">Detalle de Items ({f.items_detalle.length})</p>
                                  <table className="w-full text-[10px] border rounded">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="px-2 py-1 text-left">Item</th>
                                        <th className="px-2 py-1 text-left">Descripcion</th>
                                        <th className="px-2 py-1">Ref.</th>
                                        <th className="px-2 py-1 text-right">Cant.</th>
                                        <th className="px-2 py-1 text-right">Precio</th>
                                        <th className="px-2 py-1 text-right">Total</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {f.items_detalle.map((it, idx) => (
                                        <tr key={idx} className="border-t">
                                          <td className="px-2 py-1 font-medium">{it.nombre}</td>
                                          <td className="px-2 py-1 text-gray-600 truncate max-w-[200px]" title={it.descripcion}>{it.descripcion || '-'}</td>
                                          <td className="px-2 py-1 text-gray-400">{it.referencia || '-'}</td>
                                          <td className="px-2 py-1 text-right">{it.cantidad}</td>
                                          <td className="px-2 py-1 text-right font-mono">${fmt(it.precio)}</td>
                                          <td className="px-2 py-1 text-right font-mono font-medium">${fmt(it.total || it.precio * it.cantidad)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 border-t font-medium text-xs">
                    <td colSpan={6} className="px-3 py-2 text-right text-gray-600">
                      Total activas ({facturasAlegra.resumen?.facturas_activas || 0}):
                      <span className="text-[9px] text-gray-400 ml-1">(excluye anuladas)</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">${fmt(facturasAlegra.resumen?.total_neto)}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">${fmt(facturasAlegra.resumen?.total_monto)}</td>
                    <td className="px-3 py-2 text-right font-mono text-green-600">${fmt(facturasAlegra.resumen?.total_pagado)}</td>
                    <td className="px-3 py-2 text-right font-mono text-amber-600">${fmt(facturasAlegra.resumen?.total_saldo)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-600">No hay facturas para {mesLabel(facturasAlegra.mes_actual || mesFilter)}</p>
              <p className="text-xs text-gray-400 mt-1">Intente con otro mes o filtro</p>
            </div>
          )}
        </div>
      )}

      {/* ======== MODAL: Create/Edit Proforma ======== */}
      <Dialog open={formModal.open} onOpenChange={(o) => !saving && setFormModal({ ...formModal, open: o })}>
        <DialogContent className="max-w-[986px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {formModal.mode === 'create' ? <Plus className="w-5 h-5 text-teal-600" /> : <Edit2 className="w-5 h-5 text-amber-600" />}
              {formModal.mode === 'create' ? 'Nueva Proforma' : `Editar Proforma #${formModal.proforma?.numero_proforma}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="proforma-form">
            <div>
              <label className="block text-xs font-medium mb-1">Orden de Trabajo (OT)</label>
              <div className="flex gap-1 mb-1">
                {[{ key: 'ejecucion', label: 'En Ejecucion' }, { key: 'admin', label: 'Admin (99x)' }, { key: 'todas', label: 'Todas' }].map(opt => (
                  <button key={opt.key} onClick={() => { setOtFiltro(opt.key); loadOts(opt.key); }}
                    className={`px-2 py-0.5 rounded text-[9px] font-medium ${otFiltro === opt.key ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'}`}>{opt.label}</button>
                ))}
              </div>
              {/* Iter 291 (Carlos) — buscador texto libre: escriba "976" o "Mercedes"
                  y verá todas las opciones que incluyan ese texto. */}
              <SearchableCombobox
                items={otList.map((o) => ({
                  value: String(o.numero_ot),
                  label: `OT-${o.numero_ot} — ${(o.descripcion || '').substring(0, 60)}`,
                  subLabel: `${o.cliente_nombre || ''}${o.estado ? ` · ${o.estado}` : ''}`,
                }))}
                value={formData.ot_numero ? String(formData.ot_numero) : ''}
                onChange={(sel) => {
                  const v = sel ? sel.value : '';
                  const ot = otList.find((o) => String(o.numero_ot) === String(v));
                  setFormData((d) => {
                    const next = { ...d, ot_numero: v };
                    // Iter 402 — autocompletar la OT de las líneas vacías con la OT general elegida (cambiable por línea).
                    if (v) next.items = d.items.map((it) => (it.ot_numero ? it : { ...it, ot_numero: v }));
                    if (!ot) return next;
                    // Iter 401 — OT centralizadora: el cliente de la OT es referencia interna; no se copia a la proforma.
                    if (esOtCentralizadora(ot)) return next;
                    if (ot.cliente_alegra_id) {
                      next.cliente_alegra_id = ot.cliente_alegra_id;
                      next.cliente_rut = ot.cliente_rut || d.cliente_rut;
                      next.cliente_nombre = ot.cliente_nombre || d.cliente_nombre;
                      // Al fijar cliente por OT: intentar precargar cond_pago del maestro
                      // Iter 292.d — no matchear por RUT vacío (colisiona con clientes sin RUT).
                      const rutBusqueda = (ot.cliente_rut || d.cliente_rut || '').trim();
                      const cliMaster = rutBusqueda ? clienteList.find((c) => c.rut === rutBusqueda) : null;
                      if (cliMaster) {
                        next.contacto = d.contacto || cliMaster.contacto || '';
                        next.condicion_pago = d.condicion_pago || cliMaster.condicion_pago || '';
                      }
                      return next;
                    }
                    const otNombre = (ot.cliente_nombre || '').trim();
                    const otLower = otNombre.toLowerCase();
                    if (otNombre && otNombre.length >= 3) {
                      const cli = clienteList.find((c) => {
                        const rs = (c.razon_social || '').toLowerCase().trim();
                        if (!rs) return false;
                        if (rs === otLower) return true;
                        if (rs.startsWith(otLower) || otLower.startsWith(rs)) return true;
                        const re = new RegExp(`\\b${otLower.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}\\b`, 'i');
                        return re.test(rs);
                      });
                      if (cli) {
                        next.cliente_rut = cli.rut || '';
                        next.cliente_nombre = cli.razon_social || otNombre;
                        next.contacto = d.contacto || cli.contacto || '';
                        next.condicion_pago = d.condicion_pago || cli.condicion_pago || '';
                      } else {
                        next.cliente_nombre = otNombre;
                        next.cliente_rut = '';
                      }
                    }
                    return next;
                  });
                }}
                placeholder="Escriba OT (976) o cliente (Mercedes)…"
                emptyMessage="Sin OTs coincidentes"
                testid="proforma-ot"
              />
              {esOtCentralizadora(otList.find((o) => String(o.numero_ot) === String(formData.ot_numero))) && (
                <p className="mt-1 text-[11px] text-sky-700" data-testid="proforma-ot-centralizadora-nota">
                  OT centralizadora (multi-cliente): el cliente de la OT es referencia interna. Selecciona abajo al cliente real a facturar; se validan sus exigencias de OC/HES.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Cliente</label>
                {/* Iter 291.f (Carlos, feb-2026) — SearchableCombobox: se puede
                    escribir "OXIQUIM", "76.xxx" o cualquier texto de la razón
                    social/RUT para filtrar rápido, igual que el buscador de OT. */}
                <SearchableCombobox
                  items={clienteList.map((c) => ({
                    value: c.id,
                    label: c.razon_social,
                    subLabel: c.rut,
                  }))}
                  value={clienteSeleccionado?.id || ''}
                  onChange={(sel) => handleClienteChange(sel ? sel.value : '')}
                  placeholder="Escriba razón social o RUT…"
                  emptyMessage="Sin clientes coincidentes"
                  testid="proforma-cliente"
                />
                {/* Iter 291 — muestra los flags de exigencia del cliente elegido */}
                {clienteSeleccionado && (clienteSeleccionado.require_oc_cliente || clienteSeleccionado.require_hes) && (
                  <p className="text-[10px] text-amber-700 mt-1" data-testid="cliente-flags-warning">
                    Este cliente exige: {clienteSeleccionado.require_oc_cliente && <b>N° OC</b>}
                    {clienteSeleccionado.require_oc_cliente && clienteSeleccionado.require_hes && ' y '}
                    {clienteSeleccionado.require_hes && <b>N° HES</b>}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Nombre</label>
                <Input value={formData.cliente_nombre} onChange={(e) => setFormData(d => ({ ...d, cliente_nombre: e.target.value }))} placeholder="Razon social" />
              </div>
            </div>
            {/* Iter 291 — nuevo campo Contacto (persona receptora), aparece en el PDF. */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Contacto (persona receptora)</label>
                <Input
                  value={formData.contacto}
                  onChange={(e) => setFormData(d => ({ ...d, contacto: e.target.value }))}
                  placeholder="Nombre de la persona de contacto"
                  data-testid="proforma-contacto"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Condicion Pago</label>
                <select value={formData.condicion_pago} onChange={(e) => setFormData(d => ({ ...d, condicion_pago: e.target.value }))}
                  className="w-full border rounded px-2 py-1.5 text-xs" data-testid="proforma-condicion-pago">
                  <option value="">-- Seleccione --</option>
                  {/* Iter 291.d (Carlos, feb-2026): mismo catálogo `condiciones_pago`
                      que Proveedores y Clientes. Antes usaba `formasPagoAlegra`
                      (endpoint `/compras/formas-pago-alegra`), lo que dejaba
                      opciones distintas entre pantallas. */}
                  {condicionesPago.map((cp) => (
                    <option key={cp.codigo} value={cp.nombre}>
                      {cp.nombre}{cp.dias_plazo > 0 ? ` (${cp.dias_plazo} días)` : ''}
                    </option>
                  ))}
                  {formData.condicion_pago && !condicionesPago.some((cp) => cp.nombre === formData.condicion_pago) && (
                    <option value={formData.condicion_pago}>{formData.condicion_pago} (legado)</option>
                  )}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">
                  N OC/OS del Cliente
                  {clienteSeleccionado?.require_oc_cliente && <span className="text-red-600 ml-1">*</span>}
                </label>
                <Input value={formData.numero_oc_cliente} onChange={(e) => setFormData(d => ({ ...d, numero_oc_cliente: e.target.value }))} placeholder="OC o OS" data-testid="proforma-oc-cliente" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">
                  N HES
                  {clienteSeleccionado?.require_hes && <span className="text-red-600 ml-1">*</span>}
                </label>
                <Input value={formData.numero_hes} onChange={(e) => setFormData(d => ({ ...d, numero_hes: e.target.value }))} placeholder="HES" data-testid="proforma-hes" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold">Items</label>
                <Button variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn"><Plus className="w-3 h-3 mr-1" /> Item</Button>
              </div>
              <div className="border rounded overflow-hidden">
                {/* Iter 291 (Carlos) — Reorden de columnas:
                    Cuenta Alegra (1º) · Cant · Unidad · Precio · Descripción (5º) · Subtotal
                    Tab-order: N HES → Cuenta Alegra → Cant → Unidad → Precio → Descripción → siguiente línea.
                    La primera opción del <select> de Cuenta Alegra es en BLANCO para forzar elección. */}
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-100">
                    <th className="px-2 py-1.5 w-36 text-left">Cuenta Alegra</th>
                    <th className="px-2 py-1.5 w-28 text-left">OT</th>
                    <th className="px-2 py-1.5 w-32 text-right">Cant.</th>
                    <th className="px-2 py-1.5 w-16">Unidad</th>
                    <th className="px-2 py-1.5 text-right w-28">Precio</th>
                    <th className="px-2 py-1.5 text-left">Descripcion</th>
                    <th className="px-2 py-1.5 text-right w-24">Subtotal</th>
                    <th className="px-2 py-1.5 w-8"></th>
                  </tr></thead>
                  <tbody>
                    {formData.items.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-2 py-1">
                          <select
                            value={item.alegra_item_id || ''}
                            onChange={(e) => updateItem(idx, 'alegra_item_id', e.target.value)}
                            className="h-7 w-full border rounded text-xs px-1"
                            data-testid={`item-alegra-${idx}`}
                            tabIndex={0}
                            title="Item Alegra (define la cuenta contable de la línea)">
                            <option value="">-- Seleccione --</option>
                            {itemsAlegra.map((ia) => (
                              <option key={ia.id} value={ia.id} title={ia.categoria}>{ia.nombre}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-1">
                          {/* Iter 402 (Renzo) — OT por línea (searchbox combobox, igual que la OT general). */}
                          <SearchableCombobox
                            compact
                            items={otList.map((o) => ({ value: String(o.numero_ot), label: `OT-${o.numero_ot}`, subLabel: (o.descripcion || '').substring(0, 40) }))}
                            value={item.ot_numero ? String(item.ot_numero) : ''}
                            onChange={(sel) => updateItem(idx, 'ot_numero', sel ? sel.value : '')}
                            placeholder="OT"
                            emptyMessage="Sin OTs"
                            testid={`item-ot-${idx}`}
                          />
                        </td>
                        <td className="px-2 py-1"><Input type="number" step="0.01" min="0" value={item.cantidad} onChange={(e) => updateItem(idx, 'cantidad', parseFloat(e.target.value) || 0)} className="h-7 text-xs text-right" data-testid={`item-cant-${idx}`} /></td>
                        <td className="px-2 py-1">
                          <select value={item.unidad} onChange={(e) => updateItem(idx, 'unidad', e.target.value)} className="h-7 w-full border rounded text-xs px-1" data-testid={`item-unidad-${idx}`}>
                            <option value="un">un</option><option value="kg">kg</option><option value="m">m</option>
                            <option value="gl">gl</option><option value="hr">hr</option><option value="Serv">Serv</option>
                          </select>
                        </td>
                        <td className="px-2 py-1"><Input type="number" value={item.precio_unitario} onChange={(e) => updateItem(idx, 'precio_unitario', parseFloat(e.target.value) || 0)} className="h-7 text-xs text-right" data-testid={`item-precio-${idx}`} /></td>
                        <td className="px-2 py-1"><Input value={item.descripcion} placeholder="Descripcion" onChange={(e) => updateItem(idx, 'descripcion', e.target.value)} className="h-7 text-xs" data-testid={`item-desc-${idx}`} /></td>
                        <td className="px-2 py-1 text-right font-mono font-medium">${fmt(item.cantidad * item.precio_unitario)}</td>
                        <td className="px-2 py-1 text-center">{formData.items.length > 1 && <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600" tabIndex={-1}><X className="w-3.5 h-3.5" /></button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 flex justify-end">
                <div className="w-56 space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-gray-600">Neto:</span><span className="font-mono">${fmt(formNeto)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">IVA (19%):</span><span className="font-mono">${fmt(formIVA)}</span></div>
                  <div className="flex justify-between border-t pt-1 font-bold"><span>Total:</span><span className="font-mono text-teal-700">${fmt(formTotal)}</span></div>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Observaciones</label>
              <textarea value={formData.observaciones} onChange={(e) => setFormData(d => ({ ...d, observaciones: e.target.value }))}
                className="w-full border rounded px-2 py-1.5 text-xs h-16 resize-none" placeholder="Observaciones..." />
            </div>
            {/* Iter 291 — mensaje contextual sobre qué falta para poder crear */}
            {formModal.mode === 'create' && proformaValidationError && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="proforma-validation-msg">
                Para habilitar &quot;Crear Proforma&quot;: {proformaValidationError}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormModal({ open: false, mode: 'create', proforma: null })} disabled={saving}>Cancelar</Button>
            <Button
              onClick={handleSave}
              disabled={saving || (formModal.mode === 'create' && !!proformaValidationError)}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="save-proforma-btn"
              title={proformaValidationError || ''}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {formModal.mode === 'create' ? 'Crear Proforma' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ======== MODAL: Proforma Detail ======== */}
      <Dialog open={detailModal.open} onOpenChange={(o) => setDetailModal({ ...detailModal, open: o })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Proforma #{detailModal.proforma?.numero_proforma} {detailModal.proforma && estadoBadge(detailModal.proforma.estado)}</DialogTitle>
          </DialogHeader>
          {detailModal.proforma && (() => {
            const p = detailModal.proforma;
            const proformaTag = p.ot_numero
              ? `${p.ot_numero}-PRO${p.numero_proforma}`
              : `PRO${p.numero_proforma}`;
            const mostrarProformaTag = ['aprobada', 'facturada_en_alegra', 'facturada'].includes(p.estado);
            return (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded">
                  <p className="font-semibold mb-1"><Building className="w-4 h-4 inline mr-1" />Cliente</p>
                  <p>{p.cliente_nombre}</p>
                  <p className="text-xs text-gray-500">RUT: {p.cliente_rut}</p>
                  {p.contacto && (
                    <p className="text-xs text-gray-700 mt-1"><strong>Contacto:</strong> {p.contacto}</p>
                  )}
                </div>
                <div className="bg-gray-50 p-3 rounded space-y-1 text-xs">
                  {p.ot_numero && <p><strong>OT:</strong> {p.ot_numero}</p>}
                  {mostrarProformaTag && (
                    <p data-testid="preview-proforma-tag">
                      <strong>Proforma:</strong>{' '}
                      <span className="font-mono font-bold text-teal-700">{proformaTag}</span>
                    </p>
                  )}
                  <p><strong>OC/OS:</strong> {p.numero_oc_cliente || '-'}</p>
                  <p><strong>HES:</strong> {p.numero_hes || '-'}</p>
                  <p><strong>Pago:</strong> {p.condicion_pago}</p>
                </div>
              </div>
              <div className="bg-teal-50 p-3 rounded">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div><p className="text-xs text-gray-500">Neto</p><p className="font-bold">${fmt(p.totales?.neto)}</p></div>
                  <div><p className="text-xs text-gray-500">IVA</p><p className="font-bold">${fmt(p.totales?.iva)}</p></div>
                  <div><p className="text-xs text-gray-500">Total</p><p className="font-bold text-lg">${fmt(p.totales?.total)}</p></div>
                </div>
              </div>
              {p.items?.length > 0 && (
                <table className="w-full text-xs border">
                  <thead className="bg-gray-100"><tr><th className="px-2 py-1 text-left">Item</th><th className="px-2 py-1 text-center">OT</th><th className="px-2 py-1 text-right">Cant.</th><th className="px-2 py-1 text-right">Precio</th><th className="px-2 py-1 text-right">Subtotal</th></tr></thead>
                  <tbody>{p.items.map((it, i) => (
                    <tr key={i} className="border-t"><td className="px-2 py-1">{it.descripcion}</td><td className="px-2 py-1 text-center font-mono text-[11px]">{it.ot_numero || p.ot_numero || '—'}</td><td className="px-2 py-1 text-right">{it.cantidad} {it.unidad}</td><td className="px-2 py-1 text-right">${fmt(it.precio_unitario)}</td><td className="px-2 py-1 text-right font-medium">${fmt(it.cantidad * it.precio_unitario)}</td></tr>
                  ))}</tbody>
                </table>
              )}
              {p.folio_dte && <div className="bg-green-50 border border-green-200 rounded p-3 text-xs"><strong>Folio DTE:</strong> {p.folio_dte}</div>}
              {Array.isArray(p.envios_email) && p.envios_email.length > 0 && (
                <div className="border rounded">
                  <div className="bg-gray-50 border-b px-3 py-2 flex items-center gap-2 text-xs font-semibold">
                    <History className="w-4 h-4 text-blue-600" />
                    Historial de Envíos por Correo ({p.envios_email.length})
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    <table className="w-full text-[10px]" data-testid="envios-email-table">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-1 text-left">Fecha</th>
                          <th className="px-2 py-1 text-left">Para</th>
                          <th className="px-2 py-1 text-left">Por</th>
                          <th className="px-2 py-1 text-center">Estado</th>
                          <th className="px-2 py-1 text-left">Origen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...p.envios_email].reverse().map((e, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-2 py-1 font-mono">{(e.fecha || '').replace('T', ' ').slice(0, 16)}</td>
                            <td className="px-2 py-1" title={`TO: ${(e.to||[]).join(', ')}\nCC: ${(e.cc||[]).join(', ')}`}>
                              {(e.to || []).join(', ')}
                              {e.cc?.length > 0 && <span className="text-gray-400"> (+{e.cc.length} CC)</span>}
                            </td>
                            <td className="px-2 py-1 text-gray-600">{e.enviado_por}</td>
                            <td className="px-2 py-1 text-center">
                              {e.estado === 'enviado'
                                ? <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded">✓ Enviado</span>
                                : <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded" title={e.error}>✗ Error</span>}
                            </td>
                            <td className="px-2 py-1 text-gray-500">{e.trigger === 'auto_aprobacion' ? 'Auto (al aprobar)' : 'Manual'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div className="text-[10px] text-gray-400 border-t pt-2">
                <p>Creado: {p.created_by} ({p.created_at?.split('T')[0]})</p>
                {p.aprobado_por && <p>Aprobado: {p.aprobado_por} ({p.fecha_aprobacion?.split('T')[0]})</p>}
              </div>
            </div>
            );
          })()}
          <DialogFooter><Button variant="outline" onClick={() => setDetailModal({ open: false, proforma: null })}>Cerrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ======== MODAL: Facturar ======== */}
      <Dialog open={facturarModal.open} onOpenChange={(o) => !facturando && setFacturarModal({ ...facturarModal, open: o })}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Facturar Proforma #{facturarModal.proforma?.numero_proforma}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-teal-50 border border-teal-200 rounded p-3 text-sm">
              <p><strong>Cliente:</strong> {facturarModal.proforma?.cliente_nombre}</p>
              <p><strong>Total:</strong> ${fmt(facturarModal.proforma?.totales?.total)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Folio DTE (N Factura)</label>
              <Input value={folioDTE} onChange={(e) => setFolioDTE(e.target.value)} placeholder="Numero de folio DTE" data-testid="folio-dte-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFacturarModal({ open: false, proforma: null })}>Cancelar</Button>
            <Button onClick={handleFacturar} disabled={facturando || !folioDTE.trim()} className="bg-teal-600 hover:bg-teal-700" data-testid="confirmar-facturar-btn">
              {facturando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ======== MODAL: Asociar DTE manualmente (Admin/Gerencia) — Iter 291.k ======== */}
      <Dialog open={asociarDTEModal.open} onOpenChange={(o) => !asociando && setAsociarDTEModal({ ...asociarDTEModal, open: o })}>
        <DialogContent className="max-w-md" data-testid="asociar-dte-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-indigo-600" />
              {asociarDTEModal.proforma?.folio_dte ? 'Modificar' : 'Asociar'} folio DTE — Proforma #{asociarDTEModal.proforma?.numero_proforma}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {asociarDTEModal.proforma?.folio_dte ? (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900" data-testid="modificar-dte-banner">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Esta proforma ya está linkeada al DTE folio
                <span className="font-mono font-bold px-1">{asociarDTEModal.proforma.folio_dte}</span>.
                Al guardar, se re-linkeará al nuevo folio (queda registrado en el audit log con el folio anterior).
                El nuevo folio debe existir en la bandeja de DTEs (emisor CHIRINO STEEL).
              </div>
            ) : (
              <div className="bg-indigo-50 border border-indigo-200 rounded p-3 text-xs text-indigo-900">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Usa este flujo solo cuando el XML del DTE llegó a Chirino sin la referencia
                <span className="font-mono px-1"> Contrato: OT-PRO&lt;n&gt; </span>
                y el auto-link no lo asoció.
                Requiere que el folio ya exista en la bandeja de DTEs (emisor CHIRINO STEEL).
              </div>
            )}
            {asociarDTEModal.proforma && (
              <div className="bg-gray-50 p-3 rounded text-sm space-y-1">
                <p><strong>Cliente:</strong> {asociarDTEModal.proforma.cliente_nombre}</p>
                {asociarDTEModal.proforma.ot_numero && (
                  <p><strong>OT:</strong> {asociarDTEModal.proforma.ot_numero}</p>
                )}
                <p><strong>Total:</strong> ${fmt(asociarDTEModal.proforma.totales?.total)}</p>
                <p><strong>Alegra Invoice:</strong> {asociarDTEModal.proforma.alegra_invoice_id || '-'}</p>
                {asociarDTEModal.proforma.folio_dte && (
                  <p><strong>Folio DTE actual:</strong> <span className="font-mono">{asociarDTEModal.proforma.folio_dte}</span></p>
                )}
              </div>
            )}
            <div>
              <label className="block text-xs font-medium mb-1">
                {asociarDTEModal.proforma?.folio_dte ? 'Nuevo folio DTE' : 'Folio DTE a asociar'} <span className="text-red-500">*</span>
              </label>
              <Input
                value={asociarFolioInput}
                onChange={(e) => setAsociarFolioInput(e.target.value)}
                placeholder="Ej: 2513"
                data-testid="asociar-folio-input"
              />
              <p className="text-[10px] text-gray-400 mt-1">
                El folio debe existir en dte_documentos con emisor CHIRINO STEEL y receptor coincidente con el cliente.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">
                Motivo <span className="text-red-500">*</span>
              </label>
              <textarea
                value={asociarMotivoInput}
                onChange={(e) => setAsociarMotivoInput(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-xs h-16 resize-none"
                placeholder="Ej: El XML llegó sin Contrato porque..."
                data-testid="asociar-motivo-input"
              />
              <p className="text-[10px] text-gray-400 mt-1">Mínimo 5 caracteres. Queda en el audit log.</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setAsociarDTEModal({ open: false, proforma: null }); setAsociarFolioInput(''); setAsociarMotivoInput(''); }}
              disabled={asociando}>
              Cancelar
            </Button>
            <Button
              onClick={handleAsociarDTEManual}
              disabled={asociando || !asociarFolioInput.trim() || asociarMotivoInput.trim().length < 5}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="confirmar-asociar-dte-btn">
              {asociando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
              {asociarDTEModal.proforma?.folio_dte ? 'Guardar nuevo folio' : 'Asociar DTE'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ======== MODAL: Desaprobar Proforma ======== */}
      <Dialog open={desaprobarModal.open} onOpenChange={(o) => !desaprobando && setDesaprobarModal({ ...desaprobarModal, open: o })}>
        <DialogContent className="max-w-md" data-testid="desaprobar-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Unlock className="w-5 h-5 text-orange-600" />
              Desaprobar Proforma #{desaprobarModal.proforma?.numero_proforma}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded p-3 text-xs text-orange-800">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              La proforma volverá al estado <strong>Borrador</strong> y podrá ser editada o eliminada.
              Se guardará un registro con tu usuario, fecha y motivo.
            </div>
            {desaprobarModal.proforma && (
              <div className="bg-gray-50 p-3 rounded text-sm">
                <p><strong>Cliente:</strong> {desaprobarModal.proforma.cliente_nombre}</p>
                <p><strong>Total:</strong> ${fmt(desaprobarModal.proforma.totales?.total)}</p>
                {desaprobarModal.proforma.aprobado_por && (
                  <p className="text-xs text-gray-500 mt-1">
                    Aprobada por {desaprobarModal.proforma.aprobado_por}
                    {desaprobarModal.proforma.fecha_aprobacion
                      ? ` el ${fmtFecha(desaprobarModal.proforma.fecha_aprobacion)}`
                      : ''}
                  </p>
                )}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">
                Motivo de la desaprobación <span className="text-red-500">*</span>
              </label>
              <textarea
                value={motivoDesaprobar}
                onChange={(e) => setMotivoDesaprobar(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-xs h-20 resize-none"
                placeholder="Ej: El cliente solicitó cambio de precios / error en items / cambio de OT..."
                data-testid="motivo-desaprobar-input"
              />
              <p className="text-[10px] text-gray-400 mt-1">Mínimo 3 caracteres.</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setDesaprobarModal({ open: false, proforma: null }); setMotivoDesaprobar(''); }}
              disabled={desaprobando}>
              Cancelar
            </Button>
            <Button
              onClick={handleDesaprobar}
              disabled={desaprobando || motivoDesaprobar.trim().length < 3}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirmar-desaprobar-btn">
              {desaprobando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Unlock className="w-4 h-4 mr-2" />}
              Desaprobar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ======== MODAL: Contabilizar DTE Venta ======== */}
      <Dialog open={contabModal.open} onOpenChange={(o) => !contabilizando && setContabModal({ ...contabModal, open: o })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-teal-600" />
              Contabilizar DTE {contabModal.dte?.folio}
              {contabModal.dte?.es_exenta && (
                <span className="text-[10px] font-semibold bg-purple-100 text-purple-700 border border-purple-300 rounded px-1.5 py-0.5" data-testid="contab-exenta-badge">EXENTA · sin IVA</span>
              )}
            </DialogTitle>
          </DialogHeader>
          {contabModal.dte && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-xs text-gray-500">Cliente</p>
                  <p className="font-medium">{contabModal.dte.receptor_nombre}</p>
                  <p className="text-[10px] text-gray-400">{contabModal.dte.receptor_rut}</p>
                </div>
                <div className="bg-teal-50 p-3 rounded text-center">
                  <p className="text-xs text-teal-600">Total</p>
                  <p className="font-bold text-xl text-teal-700">${fmt(contabModal.dte.total)}</p>
                  <p className="text-[10px] text-gray-500">Neto: ${fmt(contabModal.dte.neto)}{contabModal.dte.es_exenta ? ' (exento)' : ` + IVA: $${fmt(contabModal.dte.iva)}`}</p>
                </div>
              </div>

              {/* Estado del cliente en Alegra */}
              {contabModal.dte.alegra_client_id ? (
                <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2" data-testid="contab-cliente-ok">
                  ✓ Cliente en Alegra: <b>{contabModal.dte.alegra_client_nombre || 'encontrado'}</b> (ID {contabModal.dte.alegra_client_id})
                </div>
              ) : (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="contab-cliente-falta">
                  ⚠️ El cliente (RUT {contabModal.dte.receptor_rut}) no se encontró en Alegra. Créalo en Alegra antes de contabilizar.
                </div>
              )}

              {/* Selector de ítem / cuenta contable */}
              <div>
                <p className="text-xs font-medium mb-1">Ítem / cuenta contable</p>
                <select
                  value={contabModal.itemId || '1'}
                  onChange={(e) => setContabModal({ ...contabModal, itemId: e.target.value })}
                  className="h-9 w-full border rounded text-sm px-2"
                  data-testid="contab-item-select"
                  title="Define la cuenta contable de la factura en Alegra">
                  {(itemsAlegra.length ? itemsAlegra : [{ id: '2', nombre: 'Arriendo de Inmuebles', categoria: '' }, { id: '1', nombre: 'Proyectos', categoria: '' }]).map((ia) => (
                    <option key={ia.id} value={ia.id} title={ia.categoria}>{ia.nombre}</option>
                  ))}
                </select>
                {contabModal.dte.es_exenta && (
                  <p className="text-[10px] text-purple-600 mt-1">Sugerido para exentas: <b>Arriendo de Inmuebles</b>.</p>
                )}
              </div>

              {contabModal.dte.detalle?.length > 0 && (
                <div>
                  <p className="text-xs font-medium mb-1">Detalle</p>
                  <div className="border rounded overflow-hidden">
                    <table className="w-full text-[10px]">
                      <thead className="bg-gray-100"><tr><th className="px-2 py-1 text-left">Item</th><th className="px-2 py-1 text-right">Cant.</th><th className="px-2 py-1 text-right">Precio</th><th className="px-2 py-1 text-right">Monto</th></tr></thead>
                      <tbody>{contabModal.dte.detalle.map((it, i) => (
                        <tr key={i} className="border-t"><td className="px-2 py-1">{it.nombre || it.descripcion}</td><td className="px-2 py-1 text-right">{it.cantidad}</td><td className="px-2 py-1 text-right">${fmt(it.precio_unitario)}</td><td className="px-2 py-1 text-right">${fmt(it.monto_item)}</td></tr>
                      ))}</tbody>
                    </table>
                  </div>
                </div>
              )}
              <div className="bg-teal-50 border border-teal-200 rounded p-3 text-xs text-teal-800">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Se creará la factura <b>en Alegra</b> con el folio {contabModal.dte.folio}{contabModal.dte.es_exenta ? ', sin IVA (exenta)' : ' con IVA 19%'}. Solo se marcará contabilizada si Alegra la crea correctamente.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setContabModal({ open: false, dte: null })} disabled={contabilizando}>Cancelar</Button>
            <Button onClick={() => handleContabilizarDTE(contabModal.dte)} disabled={contabilizando || !contabModal.dte?.alegra_client_id}
              className="bg-teal-600 hover:bg-teal-700" data-testid="confirmar-contabilizar-btn">
              {contabilizando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Contabilizar en Alegra
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
